import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Phone, PhoneOff, Search, MessageSquare, Instagram, Facebook } from "lucide-react";

// Card 1: Missed Calls Visual - Calling → Voicemail → Call Ended (loops)
const MissedCallsVisual = () => {
  const [step, setStep] = useState(0);
  // Steps: 0 = calling, 1 = voicemail, 2 = call ended

  useEffect(() => {
    const timings = [2000, 2500, 2000]; // calling, voicemail, ended
    let currentStep = 0;
    
    const advanceStep = () => {
      currentStep = (currentStep + 1) % 3;
      setStep(currentStep);
    };
    
    let timeout: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timeout = setTimeout(() => {
        advanceStep();
        scheduleNext();
      }, timings[currentStep]);
    };
    
    scheduleNext();
    
    return () => clearTimeout(timeout);
  }, []);

  // Smooth transition config
  const smoothTransition = {
    duration: 0.5,
    ease: "easeInOut" as const,
  };

  const fastTransition = {
    duration: 0.3,
    ease: "easeOut" as const,
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm will-change-transform">
        {/* Voicemail Card */}
        <motion.div
          className="bg-card rounded-xl border border-border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] overflow-hidden will-change-transform"
          style={{ transform: "translateZ(0)" }} // Force GPU layer
        >
          {/* Phone Call Header */}
          <div className="bg-muted/50 px-4 py-2 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Status dot with smooth color transition */}
              <div className="relative w-2 h-2">
                <motion.div
                  className="absolute inset-0 rounded-full"
                  animate={{
                    backgroundColor: step < 2 ? "rgb(239 68 68)" : "rgb(156 163 175)",
                  }}
                  transition={smoothTransition}
                  style={{ willChange: "background-color" }}
                />
                {/* Pulsing ring - only during calling */}
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      className="absolute inset-0 rounded-full bg-red-500"
                      initial={{ scale: 1, opacity: 0.8 }}
                      animate={{ scale: [1, 1.8], opacity: [0.8, 0] }}
                      exit={{ opacity: 0 }}
                      transition={{ 
                        duration: 1,
                        repeat: Infinity,
                        ease: "easeOut",
                      }}
                      style={{ willChange: "transform, opacity" }}
                    />
                  )}
                </AnimatePresence>
              </div>
              
              {/* Status text with crossfade */}
              <div className="relative h-4 w-20 overflow-hidden">
                <motion.span
                  className="absolute inset-0 text-xs text-muted-foreground flex items-center"
                  animate={{ 
                    opacity: step === 0 ? 1 : 0,
                    y: step === 0 ? 0 : -8,
                  }}
                  transition={fastTransition}
                  style={{ willChange: "transform, opacity" }}
                >
                  Calling...
                </motion.span>
                <motion.span
                  className="absolute inset-0 text-xs text-muted-foreground flex items-center"
                  animate={{ 
                    opacity: step === 1 ? 1 : 0,
                    y: step === 1 ? 0 : step < 1 ? 8 : -8,
                  }}
                  transition={fastTransition}
                  style={{ willChange: "transform, opacity" }}
                >
                  Voicemail
                </motion.span>
                <motion.span
                  className="absolute inset-0 text-xs text-muted-foreground flex items-center"
                  animate={{ 
                    opacity: step === 2 ? 1 : 0,
                    y: step === 2 ? 0 : 8,
                  }}
                  transition={fastTransition}
                  style={{ willChange: "transform, opacity" }}
                >
                  Call Ended
                </motion.span>
              </div>
            </div>
            
            {/* Time display with crossfade */}
            <div className="relative h-4 w-8 overflow-hidden">
              <motion.span
                className="absolute inset-0 text-xs text-muted-foreground font-mono flex items-center justify-end"
                animate={{ 
                  opacity: step === 0 ? 1 : 0,
                  y: step === 0 ? 0 : -8,
                }}
                transition={fastTransition}
                style={{ willChange: "transform, opacity" }}
              >
                0:08
              </motion.span>
              <motion.span
                className="absolute inset-0 text-xs text-muted-foreground font-mono flex items-center justify-end"
                animate={{ 
                  opacity: step === 1 ? 1 : 0,
                  y: step === 1 ? 0 : step < 1 ? 8 : -8,
                }}
                transition={fastTransition}
                style={{ willChange: "transform, opacity" }}
              >
                0:12
              </motion.span>
              <motion.span
                className="absolute inset-0 text-xs text-muted-foreground font-mono flex items-center justify-end"
                animate={{ 
                  opacity: step === 2 ? 1 : 0,
                  y: step === 2 ? 0 : 8,
                }}
                transition={fastTransition}
                style={{ willChange: "transform, opacity" }}
              >
                0:00
              </motion.span>
            </div>
          </div>

          {/* Call Content */}
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              {/* Phone icon container with smooth transitions */}
              <motion.div 
                className="w-12 h-12 rounded-full flex items-center justify-center relative"
                animate={{
                  backgroundColor: step >= 2 ? "hsl(var(--muted))" : "rgb(239 68 68 / 0.1)",
                }}
                transition={smoothTransition}
                style={{ willChange: "background-color" }}
              >
                {/* Vibration animation only during calling */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  animate={step === 0 ? { 
                    x: [0, -1, 1, -1, 1, 0],
                  } : { x: 0 }}
                  transition={{ 
                    duration: 0.4, 
                    repeat: step === 0 ? Infinity : 0, 
                    repeatDelay: 0.5,
                    ease: "easeInOut",
                  }}
                  style={{ willChange: "transform" }}
                >
                  {/* Phone icon with crossfade */}
                  <motion.div
                    className="absolute"
                    animate={{ 
                      opacity: step < 2 ? 1 : 0,
                      scale: step < 2 ? 1 : 0.8,
                    }}
                    transition={fastTransition}
                    style={{ willChange: "transform, opacity" }}
                  >
                    <Phone className="w-5 h-5 text-red-500" />
                  </motion.div>
                  <motion.div
                    className="absolute"
                    animate={{ 
                      opacity: step >= 2 ? 1 : 0,
                      scale: step >= 2 ? 1 : 0.8,
                    }}
                    transition={fastTransition}
                    style={{ willChange: "transform, opacity" }}
                  >
                    <PhoneOff className="w-5 h-5 text-muted-foreground" />
                  </motion.div>
                </motion.div>
              </motion.div>
              
              <div>
                <p className="font-medium text-foreground">ABC Plumbing</p>
                <p className="text-xs text-muted-foreground">Local Business</p>
              </div>
            </div>

            {/* Voicemail message with smooth height animation */}
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: "auto", marginBottom: 0 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{
                    height: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.25, ease: "easeOut" },
                  }}
                  style={{ willChange: "height, opacity", overflow: "hidden" }}
                >
                  <div className="bg-muted/50 rounded-lg p-3 border border-border">
                    <div className="flex items-center gap-2 mb-1">
                      <motion.div 
                        className="w-1.5 h-1.5 rounded-full bg-red-500"
                        animate={{ opacity: [1, 0.4, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <span className="text-xs text-muted-foreground">Recording</span>
                    </div>
                    <p className="text-sm text-foreground/80 italic">
                      "You've reached ABC Plumbing. We can't take your call right now..."
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Hang Up Button with smooth transitions */}
            <motion.div 
              className="mt-3 flex justify-center"
              animate={{ opacity: step >= 2 ? 0.5 : 1 }}
              transition={smoothTransition}
              style={{ willChange: "opacity" }}
            >
              <motion.div
                className="relative overflow-hidden rounded-full"
                animate={step === 2 ? { scale: [1, 0.95, 1] } : { scale: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                style={{ willChange: "transform" }}
              >
                {/* Background with smooth color transition */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  animate={{
                    backgroundColor: step >= 2 ? "hsl(var(--muted))" : "rgb(239 68 68)",
                  }}
                  transition={smoothTransition}
                  style={{ willChange: "background-color" }}
                />
                {/* Text container */}
                <div className="relative z-10 px-6 py-2 h-9 flex items-center justify-center">
                  <motion.span
                    className="absolute text-sm font-medium whitespace-nowrap"
                    animate={{ opacity: step < 2 ? 1 : 0 }}
                    transition={fastTransition}
                    style={{ color: "white", willChange: "opacity" }}
                  >
                    End Call
                  </motion.span>
                  <motion.span
                    className="text-sm font-medium whitespace-nowrap"
                    animate={{ opacity: step >= 2 ? 1 : 0 }}
                    transition={fastTransition}
                    style={{ color: "hsl(var(--muted-foreground))", willChange: "opacity" }}
                  >
                    Ended
                  </motion.span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Card 2: Premium Google Search Visual - Customer searches for competitor
const VoicemailToGoogleVisual = () => {
  const [typedText, setTypedText] = useState("");
  const [phase, setPhase] = useState<'idle' | 'typing' | 'searching' | 'results' | 'selected' | 'hold'>('idle');
  const searchQuery = "plumber near me";
  
  useEffect(() => {
    let isMounted = true;
    
    const runAnimation = async () => {
      if (!isMounted) return;
      
      // Reset
      setTypedText("");
      setPhase('idle');
      
      // Brief pause before starting
      await sleep(800);
      if (!isMounted) return;
      
      // Start typing
      setPhase('typing');
      
      // Type each character with human-like timing
      for (let i = 0; i <= searchQuery.length; i++) {
        if (!isMounted) return;
        setTypedText(searchQuery.slice(0, i));
        // Variable typing speed: faster for common letters, slight pauses
        const baseDelay = 80;
        const variation = Math.random() * 60 - 30; // -30 to +30ms
        const extraPause = searchQuery[i] === ' ' ? 100 : 0; // Pause on spaces
        await sleep(baseDelay + variation + extraPause);
      }
      
      if (!isMounted) return;
      
      // Brief pause after typing, then search
      await sleep(400);
      setPhase('searching');
      
      // Loading animation
      await sleep(600);
      if (!isMounted) return;
      setPhase('results');
      
      // Results appear, pause, then select winner
      await sleep(1200);
      if (!isMounted) return;
      setPhase('selected');
      
      // Hold the selected state
      await sleep(2500);
      if (!isMounted) return;
      setPhase('hold');
      
      // Restart loop
      await sleep(500);
      if (isMounted) runAnimation();
    };
    
    runAnimation();
    
    return () => { isMounted = false; };
  }, []);
  
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  return (
    <div className="relative w-full h-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm will-change-transform">
        {/* Google Search Card */}
        <motion.div
          className="bg-white rounded-2xl border border-gray-200 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.12)] overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ transform: "translateZ(0)" }}
        >
          {/* Google Header */}
          <div className="px-5 pt-5 pb-4">
            {/* Stylized Search Logo */}
            <div className="flex justify-center mb-5">
              <div className="flex items-center gap-0.5">
                <span className="text-2xl font-medium" style={{ color: "#4285F4" }}>S</span>
                <span className="text-2xl font-medium" style={{ color: "#EA4335" }}>e</span>
                <span className="text-2xl font-medium" style={{ color: "#FBBC05" }}>a</span>
                <span className="text-2xl font-medium" style={{ color: "#4285F4" }}>r</span>
                <span className="text-2xl font-medium" style={{ color: "#34A853" }}>c</span>
                <span className="text-2xl font-medium" style={{ color: "#EA4335" }}>h</span>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <div className="flex items-center bg-white rounded-full border border-gray-300 hover:shadow-md transition-shadow duration-200 px-4 py-3">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 mx-3 min-h-[24px] flex items-center">
                  <span className="text-base text-gray-900">{typedText}</span>
                  {/* Blinking cursor */}
                  <motion.div
                    className="w-0.5 h-5 bg-blue-500 ml-0.5"
                    animate={{ 
                      opacity: phase === 'typing' || phase === 'idle' ? [1, 0] : 0 
                    }}
                    transition={{ 
                      duration: 0.6, 
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  />
                </div>
                {/* Microphone icon */}
                <div className="w-6 h-6 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-500" fill="currentColor">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                </div>
              </div>
              
              {/* Search loading indicator */}
              <AnimatePresence>
                {phase === 'searching' && (
                  <motion.div
                    className="absolute -bottom-1 left-4 right-4 h-0.5 bg-gray-100 overflow-hidden rounded-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      className="h-full bg-blue-500"
                      initial={{ x: "-100%" }}
                      animate={{ x: "100%" }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          
          {/* Search Results */}
          <AnimatePresence>
            {(phase === 'results' || phase === 'selected' || phase === 'hold') && (
              <motion.div
                className="border-t border-gray-100"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                {/* Results count */}
                <motion.div
                  className="px-5 py-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <p className="text-xs text-gray-500">About 1,240,000 results (0.42 seconds)</p>
                </motion.div>
                
                {/* Result 1 - Winner */}
                <motion.div
                  className="px-5 py-3 cursor-pointer relative overflow-hidden"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
                >
                  {/* Selection highlight background */}
                  <motion.div
                    className="absolute inset-0 bg-green-50 border-l-4 border-green-500"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ 
                      opacity: phase === 'selected' || phase === 'hold' ? 1 : 0,
                      x: phase === 'selected' || phase === 'hold' ? 0 : -10,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                  
                  <div className="relative">
                    {/* URL breadcrumb */}
                    <p className="text-xs text-gray-600 mb-0.5">quickresponseplumbing.com</p>
                    
                    {/* Title */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <motion.p 
                          className="text-lg font-normal mb-1"
                          animate={{
                            color: phase === 'selected' || phase === 'hold' ? "rgb(22 163 74)" : "rgb(26 13 171)",
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          Quick Response Plumbing - 24/7 Service
                        </motion.p>
                        
                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-yellow-500 text-sm">★★★★★</span>
                          <span className="text-xs text-gray-600">4.9</span>
                          <span className="text-xs text-gray-400">(312 reviews)</span>
                        </div>
                        
                        {/* Description */}
                        <p className="text-sm text-gray-600 leading-relaxed">
                          <span className="text-green-600 font-medium">Responds in minutes</span> · Emergency plumbing services available 24/7. Licensed & insured. Free estimates.
                        </p>
                      </div>
                      
                      {/* Checkmark */}
                      <AnimatePresence>
                        {(phase === 'selected' || phase === 'hold') && (
                          <motion.div
                            className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-1"
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0 }}
                            transition={{ 
                              type: "spring", 
                              stiffness: 300, 
                              damping: 20,
                              delay: 0.2 
                            }}
                          >
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
                
                {/* Result 2 - Faded */}
                <motion.div
                  className="px-5 py-3 pb-4 border-t border-gray-50"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ 
                    opacity: phase === 'selected' || phase === 'hold' ? 0.4 : 0.8, 
                    y: 0 
                  }}
                  transition={{ delay: 0.25, duration: 0.4, ease: "easeOut" }}
                >
                  <p className="text-xs text-gray-600 mb-0.5">proplumbersinc.com</p>
                  <p className="text-base text-blue-800 mb-1">Pro Plumbers Inc.</p>
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-yellow-500 text-xs">★★★★☆</span>
                    <span className="text-xs text-gray-400">4.2 (89)</span>
                  </div>
                  <p className="text-sm text-gray-500">Professional plumbing services...</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

// Card 3: Unified Inbox Visual - Red urgent numbers with dramatic reveal
const BuriedDMsVisual = () => {
  const [phase, setPhase] = useState<'idle' | 'revealing' | 'complete' | 'resetting'>('idle');
  const [displayCounts, setDisplayCounts] = useState([0, 0, 0, 0]);
  const [totalCount, setTotalCount] = useState(0);
  const [revealedRows, setRevealedRows] = useState<number[]>([]);
  const [pulsingRow, setPulsingRow] = useState(-1);
  const [showFooter, setShowFooter] = useState(false);
  const [totalEmphasis, setTotalEmphasis] = useState(false);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: false, margin: "-50px" });
  
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  const inboxItems = [
    { icon: PhoneOff, label: 'Missed Calls', count: 6, sublabel: 'missed calls' },
    { icon: MessageSquare, label: 'Unanswered Texts', count: 12, sublabel: 'unread messages' },
    { icon: Instagram, label: 'Instagram DMs', count: 5, sublabel: 'unread DMs' },
    { icon: Facebook, label: 'Facebook Messages', count: 3, sublabel: 'unread DMs' },
  ];

  const finalCounts = [6, 12, 5, 3];
  const totalFinal = 26;

  // Smooth count animation helper
  const animateCount = (
    from: number, 
    to: number, 
    duration: number, 
    onUpdate: (val: number) => void,
    onComplete?: () => void
  ) => {
    const startTime = performance.now();
    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      onUpdate(Math.round(from + (to - from) * eased));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        onComplete?.();
      }
    };
    requestAnimationFrame(animate);
  };

  // Reset state for loop
  const resetAnimation = () => {
    setPhase('resetting');
    setDisplayCounts([0, 0, 0, 0]);
    setTotalCount(0);
    setRevealedRows([]);
    setPulsingRow(-1);
    setShowFooter(false);
    setTotalEmphasis(false);
    setTimeout(() => setPhase('idle'), 100);
  };

  // Main animation sequence
  useEffect(() => {
    if (!isInView || phase !== 'idle') return;
    
    if (prefersReducedMotion) {
      setRevealedRows([0, 1, 2, 3]);
      setDisplayCounts(finalCounts);
      setTotalCount(totalFinal);
      setShowFooter(true);
      setPhase('complete');
      return;
    }

    setPhase('revealing');
    
    const runSequence = async () => {
      // Reveal each row with 500ms delay between them
      for (let i = 0; i < 4; i++) {
        await new Promise(resolve => setTimeout(resolve, i === 0 ? 300 : 500));
        
        // Slide in row
        setRevealedRows(prev => [...prev, i]);
        
        // Wait for slide animation
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Count up the number
        const targetCount = finalCounts[i];
        await new Promise<void>(resolve => {
          animateCount(0, targetCount, 350, (val) => {
            setDisplayCounts(prev => {
              const next = [...prev];
              next[i] = val;
              return next;
            });
          }, resolve);
        });
        
        // Pulse the row briefly
        setPulsingRow(i);
        await new Promise(resolve => setTimeout(resolve, 300));
        setPulsingRow(-1);
      }
      
      // Show footer with total
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowFooter(true);
      
      // Dramatic count up for total
      await new Promise(resolve => setTimeout(resolve, 200));
      await new Promise<void>(resolve => {
        animateCount(0, totalFinal, 600, setTotalCount, resolve);
      });
      
      // Emphasis animation on total
      setTotalEmphasis(true);
      await new Promise(resolve => setTimeout(resolve, 400));
      setTotalEmphasis(false);
      
      // Hold for 2 seconds then loop
      setPhase('complete');
      await new Promise(resolve => setTimeout(resolve, 2000));
      resetAnimation();
    };
    
    runSequence();
  }, [isInView, phase, prefersReducedMotion]);

  // Calculate if we're actively receiving notifications
  const isReceiving = phase === 'revealing' && revealedRows.length < 4;
  
  return (
    <div className="relative w-full h-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm" ref={cardRef}>
        {/* Unified Inbox Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            x: pulsingRow >= 0 ? [0, -1, 1, -1, 0] : 0,
          }}
          transition={{ 
            duration: 0.5,
            x: { duration: 0.3, ease: "easeInOut" }
          }}
          className="bg-card rounded-xl border border-border overflow-visible relative"
          style={{
            boxShadow: isReceiving 
              ? '0 8px 32px -4px rgba(239, 68, 68, 0.15), 0 4px 20px -4px rgba(0,0,0,0.1)' 
              : '0 4px 20px -4px rgba(0,0,0,0.1)',
            transition: 'box-shadow 0.5s ease'
          }}
        >
          {/* Subtle red glow overlay during active receiving */}
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none"
            animate={{
              opacity: isReceiving ? [0, 0.03, 0] : 0
            }}
            transition={{
              duration: 1.5,
              repeat: isReceiving ? Infinity : 0,
              ease: "easeInOut"
            }}
            style={{
              background: 'radial-gradient(circle at center, rgba(239, 68, 68, 0.2) 0%, transparent 70%)'
            }}
          />
          
          {/* Header - always visible */}
          <div className="bg-muted/50 px-4 py-3 border-b border-border flex items-center justify-between rounded-t-xl relative z-10">
            <div className="flex items-center gap-2">
              <motion.div 
                className="w-2 h-2 rounded-full"
                animate={{
                  backgroundColor: isReceiving ? ['rgb(239 68 68)', 'rgb(239 68 68 / 0.5)', 'rgb(239 68 68)'] : 'rgb(156 163 175 / 0.4)',
                  boxShadow: isReceiving ? ['0 0 8px 2px rgba(239, 68, 68, 0.4)', '0 0 4px 1px rgba(239, 68, 68, 0.2)', '0 0 8px 2px rgba(239, 68, 68, 0.4)'] : '0 0 0px 0px transparent'
                }}
                transition={{ duration: 1, repeat: isReceiving ? Infinity : 0, ease: "easeInOut" }}
              />
              <span className="text-xs text-muted-foreground font-medium">Unified Inbox</span>
            </div>
            <motion.span 
              className="text-xs"
              animate={{
                color: isReceiving ? 'rgb(239 68 68)' : 'rgb(156 163 175)'
              }}
              transition={{ duration: 0.3 }}
            >
              {isReceiving ? 'Incoming...' : 'Today'}
            </motion.span>
          </div>

          {/* Inbox Items */}
          <div className="p-4 space-y-4 relative z-10">
            {inboxItems.map((item, idx) => {
              const isRevealed = revealedRows.includes(idx);
              const isPulsing = pulsingRow === idx;
              const isJustRevealed = isRevealed && revealedRows[revealedRows.length - 1] === idx;
              
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -30, scale: 0.95 }}
                  animate={{ 
                    opacity: isRevealed ? 1 : 0,
                    x: isRevealed ? 0 : -30,
                    scale: isRevealed ? 1 : 0.95,
                  }}
                  transition={{ 
                    duration: 0.4, 
                    ease: [0.25, 0.1, 0.25, 1],
                    scale: { duration: 0.3 }
                  }}
                  className="flex items-center gap-3"
                >
                  {/* Icon Container */}
                  <motion.div 
                    animate={{ 
                      scale: isPulsing ? [1, 1.1, 1] : 1,
                      backgroundColor: isPulsing ? 'rgba(239, 68, 68, 0.1)' : 'rgba(156, 163, 175, 0.15)'
                    }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <motion.div
                      animate={{
                        color: isPulsing ? 'rgb(239, 68, 68)' : 'rgb(156, 163, 175)'
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      <item.icon className="w-4 h-4" />
                    </motion.div>
                  </motion.div>
                  
                  {/* Label and Count */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">
                      <motion.span 
                        className="font-medium tabular-nums"
                        animate={{
                          color: displayCounts[idx] > 0 ? 'rgb(239, 68, 68)' : 'rgb(156, 163, 175)'
                        }}
                      >
                        {displayCounts[idx]}
                      </motion.span> {item.sublabel}
                    </p>
                  </div>

                  {/* Counter Badge - RED with glow */}
                  <motion.div 
                    animate={{ 
                      scale: isPulsing ? [1, 1.2, 1] : 1,
                      boxShadow: isPulsing 
                        ? ['0 0 0px 0px rgba(239, 68, 68, 0)', '0 0 20px 4px rgba(239, 68, 68, 0.4)', '0 0 0px 0px rgba(239, 68, 68, 0)']
                        : displayCounts[idx] > 0 
                          ? '0 0 8px 0px rgba(239, 68, 68, 0.2)'
                          : '0 0 0px 0px transparent'
                    }}
                    transition={{ 
                      duration: 0.4, 
                      ease: "easeOut"
                    }}
                    className="px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 min-w-[36px] text-center"
                  >
                    <motion.span 
                      className="text-xs font-bold text-red-500 tabular-nums"
                      animate={{
                        scale: isJustRevealed && displayCounts[idx] > 0 ? [1, 1.1, 1] : 1
                      }}
                      transition={{ duration: 0.15 }}
                    >
                      {displayCounts[idx]}
                    </motion.span>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Footer with Total */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: showFooter ? 1 : 0 }}
            transition={{ duration: 0.4 }}
            className="px-4 pb-4 relative z-10"
          >
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: showFooter ? 1 : 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent mb-3 origin-left" 
            />
            <motion.div 
              className="flex items-center justify-between p-2 rounded-lg"
              animate={{
                backgroundColor: totalEmphasis ? 'rgba(239, 68, 68, 0.08)' : 'transparent',
              }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-xs text-muted-foreground font-medium">Total unread</span>
              <motion.div
                className="flex items-center gap-2"
                animate={{ 
                  scale: totalEmphasis ? [1, 1.15, 1] : 1,
                  x: totalEmphasis ? [-3, 3, -3, 3, 0] : 0,
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <motion.span 
                  className="text-lg font-bold text-red-500 tabular-nums"
                  animate={{
                    textShadow: totalEmphasis 
                      ? ['0 0 0px rgba(239, 68, 68, 0)', '0 0 20px rgba(239, 68, 68, 0.8)', '0 0 10px rgba(239, 68, 68, 0.4)']
                      : '0 0 8px rgba(239, 68, 68, 0.3)'
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {totalCount}
                </motion.span>
                {phase === 'complete' && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 20 }}
                    className="text-red-500 text-sm"
                  >
                    😰
                  </motion.span>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

// Problem Card Component
const ProblemCard = ({
  label,
  headline,
  subheadline,
  Visual,
  index,
  reversed = false,
}: {
  label: string;
  headline: React.ReactNode;
  subheadline: string;
  Visual: React.ComponentType;
  index: number;
  reversed?: boolean;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const textContent = (
    <div className={reversed ? "lg:order-last" : ""}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        className="flex items-center gap-2 mb-3 md:mb-4"
      >
        <span className="text-xs md:text-sm text-muted-foreground uppercase tracking-wider font-medium">
          {label}
        </span>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-[26px] sm:text-3xl md:text-5xl font-bold text-foreground leading-tight mb-3 md:mb-6"
      >
        {headline}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-base sm:text-lg md:text-lg text-muted-foreground leading-relaxed whitespace-pre-line"
      >
        {subheadline}
      </motion.p>
    </div>
  );

  const visualContent = (
    <motion.div
      initial={{ opacity: 0, x: reversed ? -40 : 40 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.3 }}
      className={`relative h-[340px] sm:h-[400px] md:h-[520px] min-h-[340px] md:min-h-[520px] overflow-hidden mt-6 sm:mt-8 md:mt-0 ${reversed ? "lg:order-first order-last" : "order-last"}`}
    >
      <Visual />
    </motion.div>
  );

  return (
    <section ref={ref} className="py-14 md:py-24 lg:py-32 px-4 md:px-6 bg-background">
      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {textContent}
          {visualContent}
        </div>
      </div>
    </section>
  );
};

const BusinessProblemCards = () => {
  return (
    <>
      <ProblemCard
        label="The Problem"
        headline={
          <>
            How many did you <span className="ayra-accent">lose</span> today?
          </>
        }
        subheadline="Phone rings. You're with a customer. Voicemail picks up.
They hang up. Google your competitor. Gone forever."
        Visual={MissedCallsVisual}
        index={0}
      />
      
      <ProblemCard
        label="The Reality"
        headline={
          <>
            Customers don't leave voicemails <span className="ayra-accent">anymore</span>.
          </>
        }
        subheadline="They Google your competitors."
        Visual={VoicemailToGoogleVisual}
        index={1}
        reversed
      />
      
      <ProblemCard
        label="The Problem"
        headline={
          <>
            Missed calls. Unanswered texts.{"\n"}
            Buried <span className="ayra-accent">DMs</span>.
          </>
        }
        subheadline="Every one is a customer lost."
        Visual={BuriedDMsVisual}
        index={2}
      />
    </>
  );
};

export default BusinessProblemCards;
